import { GiftIcon } from "@heroicons/react/24/outline";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { BookToGive, useBooksToGive, useGiveBook } from "../api/bookTransfers";

/**
 * Shown when the user bought more than one book: they tell us who each extra
 * book is for, we create that person's account and send the invitation.
 */
export const GiveBooksCard: FC = () => {
  const { t } = useTranslation();
  const { data } = useBooksToGive();
  const books = data?.books ?? [];
  const total = books.reduce((sum, book) => sum + book.count, 0);

  if (total === 0) return null;

  return (
    <div className="flex flex-col w-full p-4 sm:p-6 bg-white rounded-lg shadow-md border-l-4 border-l-rust-50">
      <div className="flex items-center gap-3">
        <div className="bg-delicate-50 rounded-md p-1.5 sm:p-2">
          <GiftIcon className="w-5 h-5 sm:w-6 sm:h-6 text-delicate-800" />
        </div>
        <p className="text-slate-700 text-xl sm:text-2xl font-bold">
          {t("bookTransfer.title")}
        </p>
      </div>
      <p className="mt-3 text-sm sm:text-base text-slate-600">
        {t("bookTransfer.intro")}
      </p>
      <p className="mt-1 text-sm text-slate-500">
        {t("bookTransfer.remaining", { count: total })}
      </p>
      {/* One form at a time: the list refreshes after each gift */}
      <GiveBookForm key={books[0].subscription_id} book={books[0]} />
    </div>
  );
};

const GiveBookForm: FC<{ book: BookToGive }> = ({ book }) => {
  const { t } = useTranslation();
  const give = useGiveBook();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [message, setMessage] = useState("");

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    give.mutate(
      {
        subscription_id: book.subscription_id,
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        message: message.trim() || undefined,
      },
      {
        // Errors are translated and shown by the API client (errors.*)
        onSuccess: () => {
          toast.success(t("bookTransfer.success", { email: email.trim() }));
          setEmail("");
          setFirstName("");
          setLastName("");
          setMessage("");
        },
      },
    );
  };

  const inputClass =
    "mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-rust-50";

  return (
    <form
      onSubmit={submit}
      className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
    >
      <label className="sm:col-span-3 text-sm text-slate-600">
        {t("bookTransfer.email")}
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
      </label>
      <label className="text-sm text-slate-600">
        {t("bookTransfer.first_name")}
        <input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className={inputClass}
        />
      </label>
      <label className="text-sm text-slate-600">
        {t("bookTransfer.last_name")}
        <input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          className={inputClass}
        />
      </label>
      <div className="hidden sm:block" />
      <label className="sm:col-span-3 text-sm text-slate-600">
        {t("bookTransfer.message")}
        <textarea
          rows={2}
          maxLength={1000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={inputClass}
        />
      </label>
      <div className="sm:col-span-3">
        <button
          type="submit"
          disabled={give.isLoading || !email.trim()}
          className="flex w-full sm:w-80 items-center justify-center bg-rust-50 border border-rust-50 hover:bg-white text-white hover:text-rust-50 text-sm sm:text-lg rounded px-4 sm:px-6 py-2 disabled:opacity-50"
        >
          {t("bookTransfer.submit")}
        </button>
      </div>
    </form>
  );
};
